import {
  activeSet,
  canonical,
  categorySchema,
  COOLDOWN_MS,
  DomainError,
  liveItems,
  mutationSchema,
  normalizeWord,
  type Account,
  type Mutation,
  type MutationResult,
  type StudySet,
} from './model';
import {
  addDays,
  invalidatePlans,
  nextStageDelay,
  stageRounds,
  taipeiDate,
  unitId,
} from './schedule';
import { addItems, findItem } from './words';

function validateTarget(account: Account, set: StudySet, mutation: Mutation): void {
  if (!activeSet(account, set)) throw new DomainError('conflict', '此卡組已刪除或已移出複習');
  if ('wordEpoch' in mutation && set.kind === 'word' && mutation.wordEpoch !== account.wordEpoch) {
    throw new DomainError('conflict', '這筆單字紀錄屬於先前的學習，已保留但未套用');
  }
  if ('contentRevision' in mutation && mutation.contentRevision !== set.contentRevision) {
    throw new DomainError('conflict', '內容已變更，練習紀錄已保留，請重新取得內容');
  }
}

function completeUnit(
  account: Account,
  mutation: Extract<Mutation, { operation: 'completeUnit' }>,
): Record<string, unknown> {
  const planDate = mutation.planDate ?? mutation.studyDate;
  if (planDate > mutation.studyDate) throw new DomainError('invalid', '不能完成未來日期的任務');
  const unit = account.plans[planDate]?.units.find(
    (unit) => unit.reviewUnitId === mutation.targetId,
  );
  if (!unit) throw new DomainError('conflict', '找不到原日期的複習輪次');
  const set = account.sets.find((set) => set.setId === unit.setId);
  if (!set) throw new DomainError('conflict', '卡組已移除');
  validateTarget(account, set, mutation);
  const completed = account.completedUnits[unit.reviewUnitId];
  if (completed) return { alreadyCompleted: true, count: completed.count };
  if (
    unit.status !== 'pending' ||
    unit.contentRevision !== set.contentRevision ||
    unit.itemIds.join('|') !==
      liveItems(set)
        .map((item) => item.itemId)
        .join('|')
  )
    throw new DomainError('conflict', '這一輪的內容已失效');
  const ordinal = stageRounds(set.stage) - set.remaining;
  if (
    unit.reviewUnitId !== unitId(set, ordinal) ||
    mutation.occurredAt < set.cooldownUntil ||
    mutation.occurredAt < unit.cooldownUntil
  )
    throw new DomainError('conflict', '前一輪尚未完成或仍在冷卻中');
  account.completedUnits[unit.reviewUnitId] = {
    studyDate: mutation.studyDate,
    count: unit.itemCount,
    completedAt: mutation.occurredAt,
  };
  account.historyAdded += unit.itemCount;
  account.dailyHistory[mutation.studyDate] =
    (account.dailyHistory[mutation.studyDate] ?? 0) + unit.itemCount;
  set.remaining--;
  set.totalRounds++;
  if (!set.remaining) {
    set.stage++;
    set.remaining = stageRounds(set.stage);
    set.dueDate = addDays(mutation.studyDate, nextStageDelay(set.stage));
    set.cooldownUntil = 0;
  } else set.cooldownUntil = mutation.occurredAt + COOLDOWN_MS;
  invalidatePlans(account);
  return { count: unit.itemCount };
}

function apply(
  account: Account,
  mutation: Mutation,
  now: number,
  uuid: () => string,
): Record<string, unknown> {
  switch (mutation.operation) {
    case 'addItems': {
      if (mutation.payload.kind === 'word' && mutation.wordEpoch !== account.wordEpoch)
        throw new DomainError('conflict', '單字學習已重新開始，請更新資料');
      return addItems(account, mutation.payload, now, uuid);
    }
    case 'restartWords': {
      if (mutation.wordEpoch !== account.wordEpoch)
        throw new DomainError('conflict', '單字學習已重新開始');
      const affected = account.sets
        .filter((set) => activeSet(account, set) && set.kind === 'word')
        .reduce((sum, set) => sum + liveItems(set).length, 0);
      account.wordEpoch++;
      return { affected, wordEpoch: account.wordEpoch };
    }
    case 'completeUnit':
      return completeUnit(account, mutation);
    case 'editItem':
    case 'deleteItem':
    case 'setStar':
    case 'quizResult': {
      const { set, item } = findItem(account, mutation.targetId);
      validateTarget(account, set, mutation);
      if (mutation.operation === 'editItem') {
        if (
          set.kind === 'word' &&
          account.sets
            .filter((set) => activeSet(account, set) && set.kind === 'word')
            .some((set) =>
              liveItems(set).some(
                (other) =>
                  other.itemId !== item.itemId &&
                  normalizeWord(other.en) === normalizeWord(mutation.payload.en),
              ),
            )
        ) {
          throw new DomainError('conflict', '已有相同單字，請透過新增合併意思');
        }
        Object.assign(item, mutation.payload);
        set.contentRevision++;
      } else if (mutation.operation === 'deleteItem') {
        item.deleted = true;
        set.contentRevision++;
      } else if (mutation.operation === 'setStar') item.starred = mutation.payload.starred;
      else {
        if (mutation.payload.disputed) return { discarded: true };
        if (mutation.payload.assisted || !mutation.payload.correct) {
          item.correctStreak = 0;
          if (!mutation.payload.correct) item.wrongCount++;
        } else {
          item.correctStreak++;
          if (item.correctStreak >= 3) item.wrongCount = 0;
        }
      }
      return { itemId: item.itemId };
    }
    case 'deleteSet':
    case 'updateSet': {
      const set = account.sets.find((set) => set.setId === mutation.targetId);
      if (!set) throw new DomainError('conflict', '卡組已移除');
      validateTarget(account, set, mutation);
      if (mutation.operation === 'deleteSet') set.deleted = true;
      else {
        if (
          mutation.payload.categoryId &&
          !account.categories.some((cat) => cat.categoryId === mutation.payload.categoryId)
        )
          throw new DomainError('invalid', '分類不存在');
        Object.assign(set, mutation.payload);
      }
      set.contentRevision++;
      return { setId: set.setId };
    }
    case 'saveCategories': {
      if (
        mutation.baseCategories &&
        canonical(mutation.baseCategories) !==
          canonical(categorySchema.array().parse(account.categories))
      )
        throw new DomainError('conflict', '分類已在其他地方變更，草稿已保留，請重新取得分類');
      const categoryIds = new Set(mutation.payload.map((cat) => cat.categoryId));
      if (categoryIds.size !== mutation.payload.length)
        throw new DomainError('invalid', '分類 ID 重複');
      account.categories = mutation.payload;
      for (const set of account.sets)
        if (set.categoryId && !categoryIds.has(set.categoryId)) set.categoryId = '';
      return {};
    }
    case 'welcome':
      account.nickname = mutation.payload.nickname;
      account.welcomed = true;
      return {};
    case 'deleteAccount':
      account.receipts = {};
      account.sets = [];
      account.categories = [];
      account.plans = {};
      account.completedUnits = {};
      account.dailyHistory = {};
      account.historyBaseline = 0;
      account.historyAdded = 0;
      account.nickname = '';
      account.welcomed = false;
      account.deletedAt = now;
      account.wordEpoch++;
      account.sessionVersion++;
      return { deleted: true };
  }
}

export function executeMutation(
  source: Account,
  raw: unknown,
  now: number,
  uuid: () => string,
): { account: Account; result: MutationResult } {
  const parsed = mutationSchema.safeParse(raw);
  if (!parsed.success) throw new DomainError('invalid', '請求格式不合法');
  const mutation = parsed.data;
  const request = canonical(mutation);
  const receipt = source.receipts[mutation.mutationId];
  if (receipt && receipt.request !== request)
    throw new DomainError('invalid', 'mutationId 已用於不同內容');
  const accountDeletionReplay =
    mutation.operation === 'deleteAccount' &&
    !!receipt &&
    source.sessionVersion === mutation.sessionVersion + 1;
  if (mutation.sessionVersion !== source.sessionVersion && !accountDeletionReplay)
    throw new DomainError('conflict', '資料版本已更換，請重新登入');
  if (receipt) return { account: source, result: { ...receipt.result, status: 'duplicate' } };
  if (taipeiDate(mutation.occurredAt) !== mutation.studyDate || mutation.occurredAt > now + 60000)
    throw new DomainError('invalid', '學習日期不合法');
  const account: Account = JSON.parse(JSON.stringify(source));
  const result: MutationResult = { status: 'applied', data: apply(account, mutation, now, uuid) };
  invalidatePlans(account);
  account.receipts[mutation.mutationId] = { request, result };
  return { account, result };
}
